import { ReactNode, useCallback, useState } from 'react';
import { ConnectOptions } from 'twilio-video';
import { ErrorCallback } from '@uplift/types';
import { VideoContext } from '@uplift/contexts';
import useScreenShareToggle from './only-use-in-video-provider-hooks/use-screen-share-toggle';
import useHandleRoomDisconnection from './only-use-in-video-provider-hooks/use-handle-room-disconnection';
import useHandleTrackPublicationFailed from './only-use-in-video-provider-hooks/use-handle-track-publication-failed';
import useLocalTracks from './only-use-in-video-provider-hooks/use-local-tracks';
import useRestartAudioTrackOnDeviceChange from './only-use-in-video-provider-hooks/use-restart-audio-track-on-device-change';
import useRoom from './only-use-in-video-provider-hooks/use-room';
import { SelectedParticipantProvider } from './only-use-in-video-provider-hooks/use-selected-participant';
import { useVideoFilter } from '@uplift/hooks';
import { useAppointmentByIdQuery, useUserWithProfileQuery } from '@uplift/queries';
/*
 *  The hooks used by the VideoProvider component are different. The hooks
 *  in the 'VideoProvider/' directory are intended to be used by the VideoProvider component only. Using these hooks
 *  elsewhere in the application may cause problems as these hooks should not be used more than once in an application.
 */

interface VideoProviderProps {
  appointmentId: string;
  options?: ConnectOptions;
  onError: ErrorCallback;
  children: ReactNode;
}

const defaultErrorHandler = () => {
  console.log('No error handler provided.');
};

export function VideoProvider({ options, children, appointmentId, onError = defaultErrorHandler }: VideoProviderProps) {
  const [ isRoomClosed, setIsRoomClosed ] = useState(false);
  const { data: appointment, isLoading: isAppointmentLoading } = useAppointmentByIdQuery(appointmentId, { enabled: !!appointmentId });
  const { data: patient, isLoading: isPatientLoading } = useUserWithProfileQuery(appointment?.patientId, { enabled: !!appointment?.patientId });
  const { data: provider, isLoading: isProviderLoading } = useUserWithProfileQuery(appointment?.providerId, { enabled: !!appointment?.providerId });
  const isLoading = isAppointmentLoading || isPatientLoading || isProviderLoading;

  const onErrorCallback: ErrorCallback = useCallback(
    error => {
      if(error?.message === 'Room completed'){
        setIsRoomClosed(true);
      }
      console.log(`ERROR: ${error.message}`, error);
      onError(error);
    },
    [ onError ]
  );

  const {
    localTracks,
    getLocalVideoTrack,
    getLocalAudioTrack,
    isAcquiringLocalTracks,
    removeLocalAudioTrack,
    removeLocalVideoTrack,
    getAudioAndVideoTracks,
  } = useLocalTracks();

  const { room, isConnecting, connect } = useRoom(localTracks, onErrorCallback, options);

  // TODO the types for localTracks are wrong all over the place here
  const { toggleVideoFilter, isEnabled: isVideoFilterEnabled, isSupported: isVideoFilterSupported } = useVideoFilter(localTracks);

  const [ isSharingScreen, toggleScreenShare ] = useScreenShareToggle(room, onErrorCallback);

  // Register callback functions to be called on room disconnect.
  useHandleRoomDisconnection(
    room,
    onErrorCallback,
    removeLocalAudioTrack,
    removeLocalVideoTrack,
    isSharingScreen,
    toggleScreenShare,
    isVideoFilterEnabled,
    toggleVideoFilter
  );

  useHandleTrackPublicationFailed(room, onErrorCallback);
  useRestartAudioTrackOnDeviceChange(localTracks);

  return (
    <VideoContext.Provider
      value={{
        isLoading,
        room,
        appointment,
        patient,
        provider,
        isRoomClosed,
        localTracks,
        isConnecting,
        onError: onErrorCallback,
        getLocalVideoTrack,
        getLocalAudioTrack,
        connect,
        isSharingScreen,
        toggleScreenShare,
        isAcquiringLocalTracks,
        removeLocalVideoTrack,
        removeLocalAudioTrack,
        getAudioAndVideoTracks,
        toggleVideoFilter,
        isVideoFilterEnabled,
        isVideoFilterSupported
      }}
    >
      <SelectedParticipantProvider room={room}>
        {children}
      </SelectedParticipantProvider>
    </VideoContext.Provider>
  );
}
